import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiGet, apiPost, apiUpload } from '../../services/api'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import { saveOfflineSubmission, saveOfflineAttachment } from '../../services/offline'
import {
  ArrowLeft,
  Camera,
  Image,
  FileText,
  Upload,
  CheckCircle,
  AlertCircle,
  Clock,
  Trash2,
  Calendar,
  Users,
  ClipboardCheck,
  FileSpreadsheet,
  Package,
  CalendarClock,
  GraduationCap,
} from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'

interface Indicator {
  id: number
  code: string
  name: string
  target: number
  max_points: number
  points_per_unit: number
  requires_attachment: boolean
  attachment_type?: string
}

interface Period {
  id: number
  year: number
  month: number
  name?: string
  is_locked: boolean
}

interface Submission {
  id: number
  submission_date: string
  status: string
  notes?: string
  created_at: string
}

// Icon mapping for each indicator
const indicatorIcons: Record<string, React.ElementType> = {
  'C.1': Users,
  'C.2': ClipboardCheck,
  'C.3': Calendar,
  'C.4': FileSpreadsheet,
  'C.5': FileText,
  'C.6': Package,
  'C.7': CalendarClock,
  'C.8': GraduationCap,
}

// Color mapping for each indicator
const indicatorColors: Record<string, { bg: string; text: string; light: string }> = {
  'C.1': { bg: 'bg-blue-500', text: 'text-blue-500', light: 'bg-blue-100' },
  'C.2': { bg: 'bg-green-500', text: 'text-green-500', light: 'bg-green-100' },
  'C.3': { bg: 'bg-purple-500', text: 'text-purple-500', light: 'bg-purple-100' },
  'C.4': { bg: 'bg-orange-500', text: 'text-orange-500', light: 'bg-orange-100' },
  'C.5': { bg: 'bg-pink-500', text: 'text-pink-500', light: 'bg-pink-100' },
  'C.6': { bg: 'bg-cyan-500', text: 'text-cyan-500', light: 'bg-cyan-100' },
  'C.7': { bg: 'bg-amber-500', text: 'text-amber-500', light: 'bg-amber-100' },
  'C.8': { bg: 'bg-indigo-500', text: 'text-indigo-500', light: 'bg-indigo-100' },
}

// Short names for each indicator
const indicatorShortNames: Record<string, string> = {
  'C.1': 'Minutes of Meeting',
  'C.2': 'Timesheet Verification',
  'C.3': 'Daily Briefing',
  'C.4': 'QAP & Daily Report',
  'C.5': 'Monthly Report',
  'C.6': 'Asset Data Report',
  'C.7': 'P3C Schedule',
  'C.8': 'Training & Skill Matrix',
}

export default function IndicatorSubmissionPage() {
  const navigate = useNavigate()
  const { code } = useParams<{ code: string }>()
  const isOnline = useOnlineStatus()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)

  const [indicator, setIndicator] = useState<Indicator | null>(null)
  const [periods, setPeriods] = useState<Period[]>([])
  const [selectedPeriod, setSelectedPeriod] = useState<Period | null>(null)
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [submissionCount, setSubmissionCount] = useState(0)
  const [calculatedPoints, setCalculatedPoints] = useState(0)

  const [file, setFile] = useState<File | null>(null)
  const [filePreview, setFilePreview] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [submissionDate, setSubmissionDate] = useState(new Date().toISOString().split('T')[0])
  const [value, setValue] = useState(1)

  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [showUploadOptions, setShowUploadOptions] = useState(false)

  const indicatorCode = code?.toUpperCase().replace('-', '.') || ''
  const Icon = indicatorIcons[indicatorCode] || FileText
  const colors = indicatorColors[indicatorCode] || { bg: 'bg-primary-500', text: 'text-primary-500', light: 'bg-primary-100' }
  const shortName = indicatorShortNames[indicatorCode] || indicator?.name || 'KPI Submission'

  useEffect(() => {
    fetchData()
  }, [indicatorCode])

  useEffect(() => {
    if (selectedPeriod && indicator) {
      fetchSubmissions()
    }
  }, [selectedPeriod, indicator])

  const fetchData = async () => {
    try {
      const indicators = await apiGet<Indicator[]>('/kpi/indicators', { is_active: true })
      const found = indicators.find(i => i.code === indicatorCode)
      if (found) {
        setIndicator(found)
      } else {
        setError(`Indicator ${indicatorCode} not found`)
      }

      const periodsData = await apiGet<Period[]>('/kpi/periods')
      const activePeriods = periodsData.filter(p => !p.is_locked)
      setPeriods(activePeriods)

      if (activePeriods.length > 0) {
        setSelectedPeriod(activePeriods[0])
      }
    } catch (err) {
      console.error('Failed to load data:', err)
      setError('Failed to load data. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchSubmissions = async () => {
    if (!selectedPeriod?.id || !indicator?.id) return

    try {
      try {
        const scoresData = await apiGet<{
          scores: Array<{ indicator: Indicator; submission_count: number; calculated_score: number }>
        }>('/kpi/scores', { period_id: selectedPeriod.id })

        const score = scoresData.scores.find(s => s.indicator.code === indicatorCode)
        if (score) {
          setSubmissionCount(score.submission_count)
          setCalculatedPoints(score.calculated_score)
        }
      } catch (scoreErr) {
        console.error('Failed to load scores:', scoreErr)
      }

      const submissionsData = await apiGet<{ items: Submission[] }>('/kpi/submissions', {
        period_id: selectedPeriod.id,
        indicator_id: indicator.id,
        per_page: 10,
      })
      setSubmissions(submissionsData.items || [])
    } catch (err) {
      console.error('Failed to load submissions:', err)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      if (selectedFile.size > 16 * 1024 * 1024) {
        setError('File size must be less than 16MB')
        return
      }

      setFile(selectedFile)
      setError(null)
      setShowUploadOptions(false)

      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onloadend = () => {
          setFilePreview(reader.result as string)
        }
        reader.readAsDataURL(selectedFile)
      } else {
        setFilePreview(null)
      }
    }
  }

  const handleCameraCapture = () => {
    cameraInputRef.current?.click()
    setShowUploadOptions(false)
  }

  const handleGallerySelect = () => {
    fileInputRef.current?.click()
    setShowUploadOptions(false)
  }

  const removeFile = () => {
    setFile(null)
    setFilePreview(null)
  }

  const handleSubmit = async () => {
    if (!selectedPeriod || !indicator) {
      setError('Please select a period')
      return
    }

    // File is required for indicators that need attachment
    if (indicator.requires_attachment && !file) {
      setError('Please upload the required attachment')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      if (isOnline) {
        const response = await apiPost<{ id: number }>('/kpi/submissions', {
          period_id: selectedPeriod.id,
          indicator_id: indicator.id,
          submission_date: submissionDate,
          value: value,
          notes: notes || undefined,
        })

        if (file && response.id) {
          const formData = new FormData()
          formData.append('file', file)
          formData.append('submission_id', String(response.id))
          await apiUpload('/kpi/attachments', formData)
        }
      } else {
        const clientId = await saveOfflineSubmission({
          client_id: `${indicatorCode}_${Date.now()}`,
          period_id: selectedPeriod.id,
          indicator_id: indicator.id,
          submission_date: submissionDate,
          value: value,
          notes: notes || undefined,
          created_at: new Date().toISOString(),
        })

        if (file) {
          await saveOfflineAttachment(clientId, file, file.name, file.type)
        }
      }

      setSuccess(true)

      setTimeout(() => {
        setFile(null)
        setFilePreview(null)
        setNotes('')
        setSubmissionDate(new Date().toISOString().split('T')[0])
        setValue(1)
        setSuccess(false)
        fetchSubmissions()
      }, 2000)
    } catch (err) {
      console.error('Submission failed:', err)
      setError('Failed to submit. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="mobile-page flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!indicator) {
    return (
      <div className="mobile-page flex flex-col items-center justify-center p-8">
        <AlertCircle className="h-16 w-16 text-red-400 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Indicator Not Found</h2>
        <p className="text-gray-500 text-center mb-6">The indicator {indicatorCode} could not be found.</p>
        <button onClick={() => navigate('/kpi')} className="btn-primary">
          Back to KPI Hub
        </button>
      </div>
    )
  }

  const progress = Math.min((submissionCount / indicator.target) * 100, 100)

  return (
    <div className="mobile-page bg-gray-50">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        onChange={handleFileSelect}
      />
      <input
        ref={cameraInputRef}
        type="file"
        className="hidden"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
      />

      {/* Header */}
      <div className={`${colors.bg} px-4 pt-4 pb-4`}>
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate('/kpi')} className="mobile-back-btn">
            <ArrowLeft className="h-6 w-6 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-white text-lg font-semibold">{shortName}</h1>
            <p className="text-white/80 text-xs">{indicatorCode} - {indicator.name.slice(0, 40)}...</p>
          </div>
        </div>

        {/* Progress Card - inside header */}
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex items-center gap-4">
            <div className={`${colors.light} rounded-xl p-3`}>
              <Icon className={`h-8 w-8 ${colors.text}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">
                  {submissionCount} / {indicator.target}
                </span>
                <span className={`text-sm font-semibold ${colors.text}`}>
                  {calculatedPoints.toFixed(2)} pts
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Max {indicator.max_points} points
              </p>
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div
                  className={`${colors.bg} rounded-full h-2 transition-all duration-500`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="px-4 mt-4 pb-32">
        {/* Period Selection */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Select Period
          </label>
          <select
            value={selectedPeriod?.id || ''}
            onChange={(e) => {
              const period = periods.find(p => p.id === Number(e.target.value))
              setSelectedPeriod(period || null)
            }}
            className="mobile-select"
          >
            {periods.map((period) => (
              <option key={period.id} value={period.id}>
                {period.name || `${period.month}/${period.year}`}
              </option>
            ))}
          </select>
        </div>

        {/* Submission Date */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Submission Date
          </label>
          <input
            type="date"
            value={submissionDate}
            onChange={(e) => setSubmissionDate(e.target.value)}
            className="mobile-input"
          />
        </div>

        {/* Value Input (for indicators that need count > 1) */}
        {indicator.target > 1 && (
          <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Count / Quantity
            </label>
            <input
              type="number"
              min="1"
              value={value}
              onChange={(e) => setValue(Math.max(1, parseInt(e.target.value) || 1))}
              className="mobile-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              How many items are you submitting?
            </p>
          </div>
        )}

        {/* File Upload */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Attachment
            {indicator.requires_attachment && <span className="text-red-500">*</span>}
          </label>

          {!file ? (
            <button
              onClick={() => setShowUploadOptions(true)}
              className="mobile-upload-area"
            >
              <Upload className="h-10 w-10 text-gray-400 mb-2" />
              <span className="text-gray-600 font-medium">Tap to upload</span>
              <span className="text-gray-400 text-xs mt-1">
                Image, PDF, or Document (max 16MB)
              </span>
            </button>
          ) : (
            <div className="mobile-file-preview">
              {filePreview ? (
                <img src={filePreview} alt="Preview" className="mobile-file-image" />
              ) : (
                <div className="mobile-file-icon">
                  <FileText className={`h-12 w-12 ${colors.text}`} />
                </div>
              )}
              <div className="mobile-file-info">
                <p className="font-medium text-gray-900 truncate">{file.name}</p>
                <p className="text-xs text-gray-500">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button onClick={removeFile} className="mobile-file-remove">
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes or description..."
            rows={3}
            className="mobile-textarea"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <p className="text-sm text-green-700">
              {isOnline ? 'Submitted successfully!' : 'Saved offline. Will sync when online.'}
            </p>
          </div>
        )}

        {/* Offline Warning */}
        {!isOnline && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
            <p className="text-sm text-yellow-700">
              You're offline. Submission will be saved locally and synced later.
            </p>
          </div>
        )}

        {/* Recent Submissions */}
        {submissions.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Recent Submissions
            </h3>
            <div className="space-y-2">
              {submissions.slice(0, 5).map((sub) => (
                <div key={sub.id} className="bg-white rounded-xl p-3 shadow-sm flex items-center gap-3">
                  <div className={`${colors.light} rounded-lg p-2`}>
                    <FileText className={`h-5 w-5 ${colors.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(sub.submission_date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      })}
                    </p>
                    {sub.notes && (
                      <p className="text-xs text-gray-500 truncate">{sub.notes}</p>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                    sub.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {sub.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submit Button - Fixed at bottom */}
      <div className="mobile-submit-container">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || (indicator.requires_attachment && !file)}
          className={`mobile-submit-btn ${colors.bg} hover:opacity-90`}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <div className="spinner border-white" />
              Submitting...
            </span>
          ) : (
            `Submit ${indicatorCode}`
          )}
        </button>
      </div>

      {/* Upload Options Modal */}
      {showUploadOptions && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowUploadOptions(false)}
          />
          <div className="mobile-upload-modal">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Upload File</h3>
              <p className="text-sm text-gray-500">Choose upload method</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={handleCameraCapture} className="mobile-upload-option">
                <Camera className={`h-8 w-8 ${colors.text} mb-2`} />
                <span className="text-sm font-medium">Camera</span>
              </button>
              <button onClick={handleGallerySelect} className="mobile-upload-option">
                <Image className="h-8 w-8 text-green-500 mb-2" />
                <span className="text-sm font-medium">Gallery</span>
              </button>
            </div>
            <button
              onClick={() => setShowUploadOptions(false)}
              className="w-full mt-4 py-3 text-gray-600 font-medium"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  )
}
